import {
  Button,
  TextInput,
  Text,
  Loader,
  Stack,
  Center,
  Box,
  Divider,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { displayErrorMessage } from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/login";
import { useEffect } from "react";
import { useAuth } from "../authentication/AuthProvider";
import { useLocation } from "wouter";
import { Logo } from "../shared/components/Logo";
import { useSendLoginEmailMutation } from "../slices/api/usersApi";
import { getRecaptchaToken } from "../shared/recaptcha";
import { GoogleSignInButton } from "./GoogleSigninButton";
import { isValidEmail } from "../shared/utility";

const Login = () => {
  const { data } = useAppSelector((state: RootState) => state.loginForm);
  const { loading } = useAuth();
  const [_, navigate] = useLocation();
  const [sendLoginEmail] = useSendLoginEmailMutation();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return;
    dispatch(initialize({ email: "" }));
  }, []);

  const handleClickSendEmail = async () => {
    if (data === undefined) {
      console.error("Data is undefined but should not be");
      return;
    }
    if (data.email == null) {
      displayErrorMessage("Email is required.");
      return;
    }

    const token = await getRecaptchaToken();

    try {
      await sendLoginEmail({
        email: data.email,
        url: window.location.origin + "/emailLoginCompletion",
        recaptchaToken: token,
      });
      navigate("/waitForLoginEmail");
      window.localStorage.setItem("emailForSignIn", data.email);
    } catch (error) {
      displayErrorMessage("Login failed.");
      return;
    }
  };

  if (loading || !data) {
    return <Loader color="blue" />;
  }

  const isValidLoginEmail = isValidEmail(data.email ?? "");
  return (
    <Center style={{ height: "100vh" }}>
      <form onSubmit={(event) => event.preventDefault()}>
        <Stack align="center" justify="center" gap="sm">
          <Box w={100}>
            <Logo />
          </Box>
          <Text size="lg">Login to Early Bird RSVP</Text>
          <GoogleSignInButton
            onSignInSuccess={() => {
              const nextPage = localStorage.getItem("intendedUrl");
              localStorage.removeItem("intendedUrl");
              navigate(nextPage ?? "/");
            }}
            onSignInError={() => {
              displayErrorMessage("Failed to sign in with Google.");
            }}
          />
          <Text size="xs">
            Registration is automatic if you sign in with Google
          </Text>
          <Box w={300}>
            <Divider
              my="md"
              label="Or enter your email if not using Google above"
              labelPosition="center"
            />
          </Box>
          <TextInput
            mt="0"
            label="Email"
            type="email"
            autoComplete="email"
            id="email"
            name="email"
            style={{ width: "300px" }}
            value={data.email ?? ""}
            onChange={(v) => {
              dispatch(
                updateField({
                  field: "email",
                  value: v.currentTarget.value,
                })
              );
            }}
          />
          <Button
            style={{ width: "200px" }}
            onClick={handleClickSendEmail}
            disabled={!isValidLoginEmail}
          >
            Send email link to login
          </Button>
          <Button
            style={{ width: "200px" }}
            onClick={() => {
              navigate(`/loginWithPassword/${data.email}`);
            }}
            disabled={!isValidLoginEmail}
          >
            Login with password
          </Button>
          <Button
            mt="xl"
            style={{ width: "200px" }}
            onClick={() => navigate(`/register/${data.email}`)}
            disabled={!isValidLoginEmail}
          >
            Register your email
          </Button>
          <Text size="xs">
            You must register your email before using your email for logging in.
          </Text>
        </Stack>
      </form>
    </Center>
  );
};

export default Login;
